import { FC, useEffect, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import ReactTimeAgo from "react-time-ago";
import {
  faClockRotateLeft,
  faMessageCode,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Flex,
  FloatingPosition,
  Text,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";

dayjs.extend(utc);

export interface SeverityCardTimestampProps {
  date: Date;
  hasComments: boolean;
  isBlocking: boolean;
  wrap?: "nowrap" | "wrap" | "wrap-reverse";
  direction?: "row" | "column";
  color?: string;
  fontSize?: string;
  tooltipPosition?: FloatingPosition;
  tooltipWithArrow?: boolean;
}

export const SeverityCardTimestamp: FC<SeverityCardTimestampProps> = ({
  date,
  hasComments,
  isBlocking,
  direction = "row",
  color,
  wrap = "nowrap",
  fontSize = "xs",
  tooltipPosition,
  tooltipWithArrow = true,
}) => {
  const [message, setMessage] = useState<string>("");
  const { colors } = useMantineTheme();

  useEffect(() => {
    const dateStr = dayjs.utc(date).format("ddd, D MMM YYYY HH:mm:ss"); // this matches the old format exactly and is easy to change if necessary

    let firstPart = "";
    if (isBlocking) {
      firstPart = "Left blocking comment";
    } else if (hasComments && !isBlocking) {
      firstPart = "Left comment";
    } else {
      firstPart = "Found";
    }
    setMessage(`${firstPart} on ${dateStr} GMT`);
  }, [date, hasComments, isBlocking]);

  return (
    <Tooltip
      label={message}
      position={tooltipPosition}
      withArrow={tooltipWithArrow}
      withinPortal
    >
      <Flex
        direction={direction}
        gap={4}
        fz={fontSize}
        align="center"
        wrap={wrap}
      >
        <FontAwesomeIcon
          icon={hasComments || isBlocking ? faMessageCode : faClockRotateLeft}
          color={color || colors["gray"][7]}
          fixedWidth
        />
        <Text span inherit c={color || "gray.7"} miw={30} ta="center">
          <ReactTimeAgo date={date} timeStyle="mini" locale="en-US" />
        </Text>
      </Flex>
    </Tooltip>
  );
};
